import React, {useEffect, useState} from 'react';
import styles from './EledoLogs.module.scss';
import {useErrorNotyf, useShowMenu, usePagination} from "hooks";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {LOG__LEVEL_INFO, LOG__TYPES__INFO} from "model/enums";
import classnames from 'classnames';
import ReactTooltip from "react-tooltip";
import {Link} from "react-router-dom";
import {fetchLogDetail, fetchLogs} from "_reducers/logsReducer";
import {
  EledoMainPageTitle, EledoModal, EledoLoader, EledoError,
} from "components/library";

const EledoLogs = () => {
  const [
    logs, logDetail,
    itemsTotal, selectedPage,
    listLoading, detailLoading, error
  ] = useSelector(state => [
    state.logs.list, state.logs.logDetail,
    state.logs.logsTotal, state.logs.selectedPage,
    state.logs.listLoading, state.logs.detailLoading, state.logs.error,
  ], shallowEqual);
  const [detailModalDisplayed, setDetailModalDisplayed] = useState(false);
  const dispatch = useDispatch();
  
  const renderPagination = usePagination(selectedPage, itemsTotal, pageId => dispatch(fetchLogs(pageId)));

  useErrorNotyf(error);
  useShowMenu();

  useEffect(() => {
    if(logDetail.id === null) setDetailModalDisplayed(false);
  }, [logDetail]);

  const onLogLineClicked = async (logId) => {
    await dispatch(fetchLogDetail(logId));
    setDetailModalDisplayed(true);
  };

  const detailModalActions = (
    <React.Fragment>
    </React.Fragment>
  );

  const detailModalContent = detailLoading ?
    <div className="flex-center"><EledoLoader/></div>
    : (logDetail && <>
      <div className={classnames(styles.logDetail, "col-wrapper")}>
        <div className="col-50">
          <label>Time</label>
          <div>{new Date(parseInt(logDetail.date)).toLocaleString()}</div>

          <label>Template</label>
          <TemplateLink templateName={logDetail.templateName}
                        templateId={logDetail.templateId}/>

          <label>Source</label>
          <div>{logDetail.source ? logDetail.source : "-"}</div>

          <label>Type</label>
          <div>
            <i className={LOG__TYPES__INFO[logDetail.type]?.icon}/>&nbsp;
            ({LOG__TYPES__INFO[logDetail.type]?.desc})
          </div>

          <label>Level</label>
          <div>
            <i className={LOG__LEVEL_INFO[logDetail.level]?.icon}/>&nbsp;
            ({LOG__LEVEL_INFO[logDetail.level]?.desc})
          </div>
        </div>
        <div className="col-50">
          <label>Documents</label>
          <div>{logDetail.documents}</div>

          <label>Pages</label>
          <div>{logDetail.pages}</div>

          <label>Preload Time</label>
          <div>{logDetail.preloadTime} ms</div>

          <label>Generation Time</label>
          <div>{logDetail.generationTime} ms</div>

          <label>Upload Time</label>
          <div>{logDetail.uploadTime} ms</div>
        </div>
      </div>
      { logDetail.messages &&
        <div className={classnames(styles.EledoLogs, "container")}>
          { logDetail.messages.map((message, index) => (
            <div key={index} className={classnames("w-layout-grid", "data-grid", "logDetail", styles.detailGrid, LOG__LEVEL_INFO[message.level+1]?.color)}>
              <div className="_wf-table-cell" id="w-node-_8f485d4a-1d97-cad0-f4f3-52cb96d63492-dd191fb9">
                <div className={classnames("tile-title", styles.tileTitle)}>Level</div>
                <div data-tip={`Log Level: ${LOG__LEVEL_INFO[message.level+1]?.desc}`}
                      data-for={"log-type-tooltip"}>
                  <i className={LOG__LEVEL_INFO[message.level+1]?.icon}/>
                </div>
                <ReactTooltip
                  id="log-type-tooltip"
                  place="top"
                  type="dark"
                  effect="float"
                  multiline={false}
                />
              </div>
              <div className="_wf-table-cell">
                <div className={classnames("tile-title", styles.tileTitle)}>Message</div>
                <div>
                  { message.message }
                </div>
              </div>
         </div>
          ))
          }
        </div>
      }
    </>
  );

  const renderError = () =>
    <EledoError title={error?.message}
                description={(<React.Fragment>
                  We are sorry &#9785; We could not load your logs. <br/><br/>
                  {error.message.toLocaleLowerCase() === "network error" && "Please check your internet connection."}
                </React.Fragment>)}/>;

  return (
    <div className={styles.EledoLogs} data-testid="EledoLogs">
      <EledoMainPageTitle
        title="Logs"
        subtitle="Information on recent Eledo document operations."
        image="IllustrationsLogs.svg"
      />

      { error && renderError() }

      <div className="section my-content-section">

        { renderPagination() }

        { !error &&
          <div className="container">
            {renderLogsHeader()}
            {listLoading && <div className="flex-center"><EledoLoader/></div>}

            {!listLoading && <LogsTable logs={logs}
                                        onLogLineClicked={onLogLineClicked}/>
            }
          </div>
        }

        { !error && !listLoading && renderPagination() }

      </div>
      {!error &&
        <EledoModal title="Log Detail"
                    content={detailModalContent}
                    displayed={detailModalDisplayed}
                    actions={detailModalActions}
                    onDismiss={() => setDetailModalDisplayed(false)}/>
      }
    </div>
  );
}

const LogsTable = ({logs, onLogLineClicked}) => {
  if(logs && logs.length === 0) {
    return <div className="flex-center my-section">You have no logs yet.</div>
  }

  const tileClass = classnames("tile-title", styles.tileTitle);

  return logs && logs.map(log => <LogTableLine key={log.id}
                                               log={log}
                                               onLogLineClicked={onLogLineClicked}
                                               tileClass={tileClass}/>
  );
};

const LogTableLine = ({log, onLogLineClicked, tileClass}) => (
  <div className={classnames("w-layout-grid", "data-grid", "logDetail", styles.dataGrid, LOG__LEVEL_INFO[log.level]?.color)}
       onClick={() => onLogLineClicked(log.id)}>
    <div className="_wf-table-cell">
      <div className={tileClass}>Date</div>
      <div>{new Date(parseInt(log.date)).toLocaleString()}</div>
    </div>
    <div className="_wf-table-cell" id="w-node-_8f485d4a-1d97-cad0-f4f3-52cb96d63492-dd191fb9">
      <div className={tileClass}>Type</div>
      <div data-tip={`Log Type: ${LOG__TYPES__INFO[log.type]?.desc}`}
           data-for={"log-type-tooltip"}>
        <i className={LOG__TYPES__INFO[log.type]?.icon}/>
      </div>
      <ReactTooltip
        id="log-type-tooltip"
        place="top"
        type="dark"
        effect="float"
        multiline={false}
      />
    </div>

    <div className="_wf-table-cell" id="w-node-_8f485d4a-1d97-cad0-f4f3-52cb96d63492-dd191fb9">
      <div className={tileClass}>Level</div>
      <div data-tip={`Log Level: ${LOG__LEVEL_INFO[log.level]?.desc}`}
           data-for={"log-type-tooltip"}>
        <i className={LOG__LEVEL_INFO[log.level]?.icon}/>
      </div>
      <ReactTooltip
        id="log-type-tooltip"
        place="top"
        type="dark"
        effect="float"
        multiline={false}
      />
    </div>

    <div className="_wf-table-cell" id="w-node-_8f485d4a-1d97-cad0-f4f3-52cb96d63492-dd191fb9">
      <div className={tileClass}>Template</div>
      <TemplateLink templateName={log.templateName}
                    templateId={log.templateId}/>
    </div>
    <div className="_wf-table-cell">
      <div className={tileClass}>Documents / Pages</div>
      <div>{log.documents}/{log.pages}</div>
    </div>
    <div className="_wf-table-cell">
      <div className={tileClass}>Took Time (ms)</div>
      <div>{log.tookTime} ms</div>
    </div>
  </div>
);

const TemplateLink = ({templateName, templateId}) => (
  <div>{templateName ?
    <Link to={`/template/${templateId}`}>{templateName}</Link> : "-"
  }</div>
)

const renderLogsHeader = () => (
  <div className={classnames("w-layout-grid", "header-grid", styles.headerGrid)}>
    <div className="_wf-table-cell">Date</div>
    <div className="_wf-table-cell">Type</div>
    <div className="_wf-table-cell">Level</div>
    <div className="_wf-table-cell">Template</div>
    <div className="_wf-table-cell">Documents / Pages</div>
    <div className="_wf-table-cell">Took Time</div>
  </div>
);

export default EledoLogs;
